/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  ApplyChannelData, ChannelDisableEnum, EquityPathType, ProductCapitalItem, RateTypeEnum,
  RouteMarketEnum,
  UserSceneEnum,
} from '@/api/apply-product/types';
import { AdjustTypeEnum } from '@/dict/quotaIncrease-type';
import ProductCommodity, { JUZI_COMMODITY, XL_COMMODITY } from '@/shared/commodity';
import { ubtSendError } from '@/shared/utils/ubt';
import Vue from 'vue';
import { RouterV2Enum } from '@/router/router-names';
import { CouponTypeEnum } from '@/api/coupon/types';
import { LoanBankCard, UserSceneConfig } from './types';

const productIdConfig: { [key: string]: { name: string; tel: string } } = {
  // 桔子
  [JUZI_COMMODITY]: {
    name: '桔多多',
    tel: '400-107-1666',
  },
  // 新流
  [XL_COMMODITY]: {
    name: '新橙优品',
    tel: '400-001-5050',
  },
};

export default productIdConfig;

export const displayLoadingText = '加载中...';

export const displayPlanText = '计算中...';

/**
 * 双融担模式-ubt公用上报参数
 * @param channelList
 * @returns
 */
export const getDoubleModeCommonUbtParams = (channelList: ApplyChannelData[]): Dictionary => {
  if (channelList && channelList.length) {
    const autarkyChannel = channelList
      .find((c) => c.productId === ProductCommodity.AUTARKY_B_COMMODITY);
    const responseId = (autarkyChannel && autarkyChannel.fundRouteResponseId) || '';
    let typeOneFunderInfo = '';
    let typeTwoFunderInfo = '';
    /** 默认选择双通道的值：1 = 极速审核通道，2 = 常规通道，0 = 无权益模块 */
    let defaultSelectDoublePassage = EquityPathType.NORMAL;

    if (autarkyChannel && autarkyChannel.capitalList && autarkyChannel.capitalList.length) {
      const { capitalList, defaultSelectCapitalIndex = 0, disabled } = autarkyChannel;
      /** 双通道是否有资格购买权益: 渠道可用 + 有权益资方 */
      const doublePassageCanBuyRights = disabled === ChannelDisableEnum.AVAILABLE
        && capitalList.some((it) => it.needRight);

      capitalList.forEach((c) => {
        if (c && c.rateType === RateTypeEnum.CAPITAL_TYPE_24) {
          typeOneFunderInfo = c.capitalCode || '';
        } else if (c && c.rateType === RateTypeEnum.CAPITAL_TYPE_36) {
          typeTwoFunderInfo = c.capitalCode || '';
        }
      });

      if (doublePassageCanBuyRights) {
        defaultSelectDoublePassage = capitalList[defaultSelectCapitalIndex].needRight
          ? EquityPathType.BUY_RIGHTS : EquityPathType.NOT_NEED_RIGHT;
      }
    }

    return {
      typeOneFunderInfo,
      typeTwoFunderInfo,
      productId: channelList.map((c) => c.productId).join('_'),
      responseId,
      defaultSelectDoublePassage,
    };
  }

  return {};
};

/**
 * 当前提现金额使用的临时额度数值（临时额度使用规则：借款时会优先使用固定额度，超过固定额度部分则会使用临时额度）
 */
export const getSnapQuotaAmount = (channel?: ApplyChannelData, applyAmount?: string): number => {
  if (channel && channel.adjustQuota && applyAmount) {
    const { availableAmount, adjustQuota } = channel;
    const { adjustType, adjustValue } = adjustQuota;

    if (adjustType === AdjustTypeEnum.TEMP) {
      const willUseAmount = Number(applyAmount) - (Number(availableAmount) - Number(adjustValue));

      if (willUseAmount > 0) {
        return willUseAmount;
      }
    }
  }

  return 0;
};

/** 获取选择银行卡的关键信息 */
export const getBankCrucialInfo = (bankCard?: LoanBankCard): Dictionary => {
  if (bankCard) {
    return {
      bankName: bankCard.bankName,
      bankId: bankCard.bankId,
      bankNo: bankCard.bankNo,
    };
  }

  return {};
};

/**
 * 检查服务端返回CapitalList是否有权益优先级的排序
 * @param capitalList
 */
export const checkDoubleModeCapitalListHasSort = (capitalList: ProductCapitalItem[]): void => {
  /** 延迟检测正确性，避免影响主流程 */
  setTimeout(() => {
    if (capitalList && capitalList.length) {
      const hasRightIdx = capitalList.findIndex((it) => it.needRight);

      if (hasRightIdx > 0) {
        ubtSendError('Has right capital not ranked first!');
      }
    }
  }, 0);
};

export const SessionStorageKeys = {
  H5_BIO_DATA: 'h5BioData',
  BIND_BANK_CARD_BEFORE_CONTEXT: 'bind_bank_card_before_context',
  BACK_QUICK_BIND__FINAL_RESULT: 'back_quick_bind__final_result',
};
/**
 * 自定义上报-信贷资产-自营提现权益tab检测中断
 * @param that 实例
 * @param params 当前数据状态
 */
export const customReportZiyingRightTabCheckBreak = (that: Vue, params: Dictionary): void => {
  /** 延迟上报，避免影响主流程 */
  setTimeout(() => {
    that.$ubtCustomEvent('finance_custom_ziying_right_tab_check_break', {
      params: JSON.stringify(params),
    });
  }, 500);
};

/** 双融担模式，获取ProductName */
export const getProductName = (
  channel?: ApplyChannelData,
  selectCapital?: ProductCapitalItem,
): string => {
  if (selectCapital && channel && channel.productId === selectCapital.productId) {
    const { capitalListDetail = [] } = selectCapital;
    try {
      return `${capitalListDetail[0].capitalName || ''}${selectCapital.routeMarket === RouteMarketEnum.SERIAL ? '等' : ''}`;
    } catch (err) {
      ubtSendError(err as Error);
      return channel.productName;
    }
  }

  return channel ? channel.productName : '';
};

/**
 * 提现页渠道列表-初始化处理：
 * 1. 渠道多资方capitalList字段处理，capitalCode: 70001_70002_70003
 * 2. defaultSelectCapitalIndex 0 -> 兜底处理
 * 3. capitalList24权益资方排序检测
 * 4. 双融担渠道文案-productName 展示处理，添加"xxx等"
 */
export const formatChannelList = (channelList: ApplyChannelData[]): ApplyChannelData[] => {
  const makeChannelList = [];
  for (let i = 0; i < channelList.length; i++) {
    const item = channelList[i];

    /** 若capitalList.length有值，则认为是双融担模式 */
    if (item.capitalList && item.capitalList.length) {
      const capitalList = item.capitalList.map((it) => {
        const { routeMarket, capitalListDetail = [] } = it;

        return {
          ...it,
          productId: item.productId,
          capitalListDetail,
          capitalCode: routeMarket === RouteMarketEnum.SERIAL
            ? capitalListDetail.map((detail) => detail.capitalCode).join('_') : capitalListDetail[0].capitalCode,
        };
      });
      /** 若是服务未返回默认资方，则兜底：默认选中第一个资方 */
      const defaultSelectCapitalIndex = item.defaultSelectCapitalIndex || 0;
      /** 检查capitalList是否有排序 */
      checkDoubleModeCapitalListHasSort(capitalList);

      /** 优先取第一个为选中资方(24+权益 服务端会排序第一) */
      makeChannelList.push({
        ...item,
        productName: getProductName(item, capitalList[defaultSelectCapitalIndex]),
        ...capitalList[defaultSelectCapitalIndex],
        capitalList,
        defaultSelectCapitalIndex,
      } as ApplyChannelData);
    } else {
      makeChannelList.push(item);
    }
  }

  return makeChannelList;
};

/** 当从返回 以下配置的页面路由，返回至提现页时，属于back返回场景 */
export const applyPorductIsBackFromRoutes: (string | RouterV2Enum) [] = [
  RouterV2Enum.cardList,
  RouterV2Enum.applyResult,
  RouterV2Enum.selectedCoupon,
  RouterV2Enum.cardBind,
  RouterV2Enum.preApplyChoose,
  RouterV2Enum.myBillAll,
  RouterV2Enum.verifyCredit,
  RouterV2Enum.marketPage,
  RouterV2Enum.withdrawalWaiting,
  RouterV2Enum.alipayCreditGuide,
];

/** 券类型优先级顺序列表，越往后越高 */
export const couponTypePriorityList = [
  CouponTypeEnum.X_DAYS_Y_AMOUNT,
  CouponTypeEnum.X_AMOUNT_Y_PERIOD,
  CouponTypeEnum.HELLO_PETTY_CASH_INTEREST_FREE,
];

/** 提现页-常规场景配置 */
export const NORMAL_SCENE_CONFIG: UserSceneConfig = {
  moneyAmountDisable: false,
  moneyTipsHidden: false,
  couponCancelDisable: false,
};

/** 提现页-零用钱场景配置 */
export const POCKET_MONEY_SCENE_CONFIG: UserSceneConfig = {
  moneyAmountDisable: true,
  moneyTipsHidden: true,
  couponCancelDisable: true,
};

/** 用户场景交互配置映射 */
export const USER_SCENE_CONFIG_MAP: Record<UserSceneEnum, UserSceneConfig> = {
  [UserSceneEnum.NORMAL]: NORMAL_SCENE_CONFIG,
  [UserSceneEnum.POCKET_MONEY]: POCKET_MONEY_SCENE_CONFIG,
};

/** 支持计算优惠折扣的优惠券类型 */
export const supportCalcDiscountCouponTypes = [
  CouponTypeEnum.PERIOD,
  CouponTypeEnum.X_DAYS_Y_AMOUNT,
  CouponTypeEnum.X_AMOUNT_Y_PERIOD,
  CouponTypeEnum.HELLO_PETTY_CASH_INTEREST_FREE,
];
